import React, { useState } from "react";
import { FormControl, TextField } from "@mui/material";
import { Autocomplete } from "@mui/lab";
import { FieldAttributes, useField, getIn } from "formik";
import { Target, createTarget } from "../../../models/Target";

type UploadTargetSelectProps = {
  label: string;
  targets: Target[];
  autoFocus?: boolean;
} & FieldAttributes<{}>;

export const UploadTargetSelect: React.FC<UploadTargetSelectProps> = ({
  label,
  targets,
  autoFocus = false,
  ...props
}) => {
  const [field, meta, helpers] = useField<Target>(props.name);
  const { value, onChange, ...fieldProps } = field;

  const targetName = (target: Target) => {
    let name = "";
    if (target.name.length > 0) name += target.name;
    if (target.description.length > 0) name += " (" + target.description + ")";
    return name;
  };

  const [inputValue, setInputValue] = useState(targetName(value));

  // we have to use Formik::getIn() .. meta.error is an object, not a string
  const errorText =
    getIn(meta.error, "name") && meta.touched ? getIn(meta.error, "name") : "";

  const handleChange = (event: React.ChangeEvent<{}>, value: Target | null) => {
    if (!value) {
      helpers.setValue(createTarget(), true);
      return;
    }
    const target = targets.find((target) => target.id === value.id);
    if (target !== undefined) helpers.setValue(target);
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <Autocomplete
          value={value || null}
          options={targets.sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          )}
          getOptionLabel={targetName}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          isOptionEqualToValue={(option: Target, value: Target) =>
            value.name ? option.id === value.id : true
          }
          renderInput={(params) => (
            <TextField
              {...params}
              {...fieldProps}
              label={label}
              variant="outlined"
              helperText={errorText}
              error={!!errorText}
              autoFocus={autoFocus}
            />
          )}
        />
      </FormControl>
    </div>
  );
};
